import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { DataGrid, type GridColDef, GridToolbar, type GridPaginationModel } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import type { Ground, PaginatedResponse } from '../../models';
import { groundApi } from '../../services/api';

const Grounds: React.FC = () => {
  const [grounds, setGrounds] = useState<Ground[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [editingGround, setEditingGround] = useState<Partial<Ground>>({});
  const [isNew, setIsNew] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [isSearching, setIsSearching] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);

  const fetchGrounds = useCallback(async (page = paginationModel.page, pageSize = paginationModel.pageSize, search = searchTerm) => {
    setLoading(true);
    try {
      const response = await groundApi.getAll({ 
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
        search: search
      });
      
      const typedResponse = response.data as PaginatedResponse<Ground>;
      setGrounds(typedResponse.results);
      setTotalCount(typedResponse.count);
    } catch (error) {
      console.error('Error fetching grounds:', error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
    fetchGrounds(newModel.page, newModel.pageSize);
  }, [fetchGrounds]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setIsSearching(true);
    if (searchDebounce) clearTimeout(searchDebounce);
    const timeout = setTimeout(() => {
      fetchGrounds(0, paginationModel.pageSize, newSearchTerm);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      setIsSearching(false);
    }, 400);
    setSearchDebounce(timeout);
  };

  useEffect(() => {
    fetchGrounds();
  }, [fetchGrounds]);

  const handleEdit = (ground: Ground) => {
    setEditingGround(ground);
    setIsNew(false);
    setOpen(true);
  };

  const handleAdd = () => {
    setEditingGround({
      name: '',
      city: '',
      country: '',
      is_international: false,
    });
    setIsNew(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setEditingGround((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        await groundApi.create(editingGround);
      } else {
        await groundApi.update(editingGround.id!, editingGround);
      }
      fetchGrounds();
      setOpen(false);
    } catch (error) {
      console.error('Error saving ground:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'image',
      headerName: 'Image',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        return params.row.image ? (
          <img
            src={params.row.image}
            alt={params.row.name}
            style={{
              width: '40px',
              height: '40px',
              objectFit: 'cover',
              borderRadius: '50%',
            }}
          />
        ) : (
          <Box sx={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.300',
            color: 'white',
            borderRadius: '50%',
            fontWeight: 600,
            fontSize: 20,
          }}>
            {params.row.name.charAt(0)}
          </Box>
        );
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => (
        <Typography fontWeight={600} fontSize={16}>{params.value}</Typography>
      ),
    },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'country', headerName: 'Country', width: 150 },
    { field: 'capacity', headerName: 'Capacity', width: 120 },
    {
      field: 'is_international',
      headerName: 'International',
      width: 120,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton color="primary" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Matches">
            <IconButton color="secondary" component="a" href={`/matches?ground=${params.row.id}`}
              target="_blank">
              <SportsCricketIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          Grounds
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => fetchGrounds()}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Ground
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search grounds by name, city, or country..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      <Paper sx={{ height: 'calc(100% - 120px)', width: '100%' }}>
        <DataGrid
          rows={grounds}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalCount}
          disableColumnFilter
          density="compact"
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f0f7ff',
            },
            '& .MuiDataGrid-cell': {
              py: 0.5,
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
            },
          }}
        />
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{isNew ? 'Add New Ground' : 'Edit Ground'}</DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2, mt: 1 }} elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Ground Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="name"
                label="Name"
                fullWidth
                value={editingGround.name || ''}
                onChange={handleInputChange}
                required
              />
              <TextField
                name="small_name"
                label="Short Name"
                fullWidth
                value={editingGround.small_name || ''}
                onChange={handleInputChange}
              />
              <TextField
                name="city"
                label="City"
                fullWidth
                value={editingGround.city || ''}
                onChange={handleInputChange}
                required
              />
              <TextField
                name="country"
                label="Country"
                fullWidth
                value={editingGround.country || ''}
                onChange={handleInputChange}
              />
              <TextField
                name="capacity"
                label="Capacity"
                fullWidth
                type="number"
                value={editingGround.capacity || ''}
                onChange={handleInputChange}
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_international"
                    checked={editingGround.is_international || false}
                    onChange={handleInputChange}
                  />
                }
                label="International Venue"
              />
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Grounds;
